import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface PermissionGroup {
  id: string;
  name: string;
  permissions: string[];
}

interface PermissionContextType {
  permissions: string[];
  groups: PermissionGroup[];
  isLoading: boolean;
  isSuperuser: boolean;
  hasPermission: (permission: string | string[], requireAll?: boolean) => boolean;
  canAccess: (permission: string | string[]) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

/**
 * Access permission state and helpers.
 */
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

/**
 * Hook to check a single permission
 */
export const useHasPermission = (permission: string): boolean => {
  const { hasPermission, isLoading } = usePermissions();
  if (isLoading) return false;
  return hasPermission(permission);
};

/**
 * Hook to check multiple permissions (any or all)
 */
export const useCanAccess = (permissions: string[], requireAll = false): boolean => {
  const { hasPermission, isLoading } = usePermissions();
  if (isLoading) return false;
  return hasPermission(permissions, requireAll);
};

interface PermissionProviderProps {
  children: ReactNode;
}

/**
 * Provides permissions fetched from the API.
 */
export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const {
    bootstrap,
    isAuthenticated,
    isLoading: authLoading,
    refreshBootstrap,
    user,
  } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuperuser, setIsSuperuser] = useState(false);

  const fetchPermissions = useCallback(async () => {
    if (authLoading) {
      setIsLoading(true);
      return;
    }

    if (!isAuthenticated) {
      setPermissions([]);
      setGroups([]);
      setIsSuperuser(false);
      setIsLoading(false);
      return;
    }

    if (!bootstrap) {
      setIsLoading(true);
      return;
    }

    const bootstrapPermissions = bootstrap.permissions;
    setIsSuperuser(Boolean(bootstrapPermissions?.isSuperuser || bootstrapPermissions?.isSystemAdmin));

    if (bootstrapPermissions?.isSuperuser) {
      setPermissions(['*']);
    } else {
      setPermissions(bootstrapPermissions?.permissions || []);
    }
    setGroups(bootstrapPermissions?.groups || []);
    setIsLoading(false);
  }, [authLoading, bootstrap, isAuthenticated]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions, user]);

  /**
   * Check if user has permission(s)
   * @param permission - Single permission or array of permissions
   * @param requireAll - If true, requires ALL permissions. If false, requires ANY.
   */
  const hasPermission = useCallback((permission: string | string[], requireAll = false): boolean => {
    // Superuser has all permissions
    if (isSuperuser || permissions.includes('*')) {
      return true;
    }

    const perms = Array.isArray(permission) ? permission : [permission];
    
    if (requireAll) {
      return perms.every(p => permissions.includes(p));
    }
    return perms.some(p => permissions.includes(p));
  }, [permissions, isSuperuser]);

  /**
   * Alias for hasPermission - checks if user can access a feature
   */
  const canAccess = useCallback((permission: string | string[]): boolean => {
    return hasPermission(permission, false);
  }, [hasPermission]);

  const refreshPermissions = useCallback(async () => {
    if (!isAuthenticated) {
      setPermissions([]);
      setGroups([]);
      setIsSuperuser(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      await refreshBootstrap();
    } catch (error) {
      console.error('Failed to refresh permissions from auth bootstrap:', error);
      setIsLoading(false);
    }
  }, [isAuthenticated, refreshBootstrap]);

  const value: PermissionContextType = {
    permissions,
    groups,
    isLoading,
    isSuperuser,
    hasPermission,
    canAccess,
    refreshPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Component to conditionally render based on permissions
 */
interface RequirePermissionProps {
  permission: string | string[];
  requireAll?: boolean;
  fallback?: ReactNode;
  children: ReactNode;
}

export const RequirePermission: React.FC<RequirePermissionProps> = ({
  permission,
  requireAll = false,
  fallback = null,
  children,
}) => {
  const { hasPermission, isLoading } = usePermissions();

  if (isLoading) {
    return null;
  }

  if (!hasPermission(permission, requireAll)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * HOC to protect components with permission checks
 */
export function withPermission<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  permission: string | string[],
  requireAll = false,
  FallbackComponent?: React.ComponentType
) {
  return function WithPermissionComponent(props: P) {
    const { hasPermission, isLoading } = usePermissions();

    if (isLoading) {
      return null;
    }

    if (!hasPermission(permission, requireAll)) {
      if (FallbackComponent) {
        return <FallbackComponent />;
      }
      return null;
    }

    return <WrappedComponent {...props} />;
  };
}

export default PermissionContext;
